import * as THREE from "three";
import type { QualitySettings } from "@/game/QualityPresets";
import { cloneModel, loadModel } from "@/player/ModelLoader";

const CYAN = 0x35e6ff;
const MAGENTA = 0xff3fd8;
const GLOW_GREEN = 0x35ff9c;

const FLOOR_RADIUS = 30;

// Kenney "Nature Kit" (CC0 — see client/public/models/nature/License.txt).
// Flat-shaded, no textures, matching the pack's own low-poly art direction.
const TREE_MODELS = ["tree_oak.glb", "tree_tall.glb", "tree_pineTallA.glb", "tree_pineTallB.glb", "tree_detailed.glb"];
const ROCK_MODELS = ["rock_largeA.glb", "rock_largeC.glb", "rock_smallA.glb", "rock_smallC.glb"];
const UNDERGROWTH_MODELS = ["plant_bushLarge.glb", "plant_bushSmall.glb", "grass_large.glb", "stump_round.glb"];
const FLOWER_MODELS = ["flower_purpleA.glb", "flower_redA.glb", "flower_yellowA.glb"];
const NATURE_PATH = (file: string) => `/models/nature/${file}`;

/** Small deterministic PRNG (mulberry32) so the forest layout is stable across reloads instead of reshuffling every visit. */
function seededRandom(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface ScatterProp {
  urls: string[];
  count: number;
  radiusRange: [number, number];
  scaleRange: [number, number];
}

/**
 * Builds the "Digital Forest" arena — a bioluminescent night-time clearing
 * (Kenney Nature Kit props) that keeps the game's cyan/magenta glow identity
 * while replacing the earlier sci-fi tower arena (spec-driven redesign).
 * Loads GLBs asynchronously; falls back gracefully (empty prop slot) if one
 * fails, per the Asset Fallback rule — never blocks the rest of the scene.
 */
export class Arena {
  private group = new THREE.Group();
  private particles: THREE.Points | null = null;

  constructor(private readonly scene: THREE.Scene, private readonly quality: QualitySettings) {}

  build(): void {
    this.scene.background = new THREE.Color(0x8fd9d0);
    if (this.quality.fog) {
      this.scene.fog = new THREE.FogExp2(0x9fe0d6, 0.012);
    }

    this.buildLighting();
    this.buildGround();
    this.buildForest();
    this.buildParticles();
    this.scene.add(this.group);
  }

  private buildLighting(): void {
    const hemi = new THREE.HemisphereLight(0xdcfff2, 0x2a5a3a, 1.5);
    this.group.add(hemi);

    const fill = new THREE.AmbientLight(0xffffff, 0.5);
    this.group.add(fill);

    const sun = new THREE.DirectionalLight(0xfff6d8, 1.6);
    sun.position.set(10, 24, 10);
    sun.castShadow = this.quality.shadows;
    if (this.quality.shadows) {
      sun.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
      sun.shadow.camera.near = 1;
      sun.shadow.camera.far = 80;
      sun.shadow.camera.left = -30;
      sun.shadow.camera.right = 30;
      sun.shadow.camera.top = 30;
      sun.shadow.camera.bottom = -30;
    }
    this.group.add(sun);

    // Small bioluminescent sparkle accents — keeps a touch of the "digital
    // world" identity alive without dominating the bright, cheerful forest.
    const rim = new THREE.PointLight(MAGENTA, 0.5, 30, 2);
    rim.position.set(-10, 3, -10);
    this.group.add(rim);

    const rim2 = new THREE.PointLight(CYAN, 0.5, 30, 2);
    rim2.position.set(10, 3, 10);
    this.group.add(rim2);
  }

  private buildGround(): void {
    const floorGeo = new THREE.CircleGeometry(FLOOR_RADIUS, 48);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x4caf6e,
      roughness: 0.9,
      metalness: 0,
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = this.quality.shadows;
    this.group.add(floor);

    // Faint glowing boundary — a "digital forest" clearing edge rather than a hard sci-fi grid.
    const ringGeo = new THREE.RingGeometry(29.3, 30, 64);
    const ringMat = new THREE.MeshBasicMaterial({ color: GLOW_GREEN, side: THREE.DoubleSide, transparent: true, opacity: 0.45 });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.02;
    this.group.add(ring);
  }

  /** Scatters nature-kit props in rings around the play area, leaving the central hunting ground (~radius 18) clear. */
  private buildForest(): void {
    const rand = seededRandom(1337);
    const scatter: ScatterProp[] = [
      { urls: TREE_MODELS, count: 26, radiusRange: [21, 29], scaleRange: [0.9, 1.6] },
      { urls: ROCK_MODELS, count: 14, radiusRange: [17, 28], scaleRange: [0.8, 1.4] },
      { urls: UNDERGROWTH_MODELS, count: 20, radiusRange: [14, 27], scaleRange: [0.8, 1.3] },
      { urls: FLOWER_MODELS, count: 24, radiusRange: [10, 26], scaleRange: [0.9, 1.2] },
    ];

    for (const { urls, count, radiusRange, scaleRange } of scatter) {
      for (let i = 0; i < count; i++) {
        const url = urls[Math.floor(rand() * urls.length)];
        const angle = rand() * Math.PI * 2;
        const radius = radiusRange[0] + rand() * (radiusRange[1] - radiusRange[0]);
        const scale = scaleRange[0] + rand() * (scaleRange[1] - scaleRange[0]);
        const rotationY = rand() * Math.PI * 2;
        this.placeProp(url, angle, radius, scale, rotationY);
      }
    }
  }

  private placeProp(file: string, angle: number, radius: number, scale: number, rotationY: number): void {
    loadModel(NATURE_PATH(file))
      .then((loaded) => {
        const { scene } = cloneModel(loaded);
        scene.position.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
        scene.rotation.y = rotationY;
        scene.scale.setScalar(scale);
        scene.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          if (mesh.isMesh) {
            mesh.castShadow = this.quality.shadows;
            mesh.receiveShadow = this.quality.shadows;
          }
        });
        this.group.add(scene);
      })
      .catch((err) => console.warn(`[Arena] failed to load prop ${file}`, err));
  }

  private buildParticles(): void {
    // Low, drifting fireflies rather than rising data-motes — fits the forest setting.
    const count = this.quality.particleCount;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 50;
      positions[i * 3 + 1] = 0.3 + Math.random() * 3.5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 50;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({ color: GLOW_GREEN, size: 0.08, transparent: true, opacity: 0.8 });
    this.particles = new THREE.Points(geo, mat);
    this.group.add(this.particles);
  }

  update(dt: number, elapsed: number): void {
    if (this.particles) {
      this.particles.rotation.y += dt * 0.015;
      const positions = this.particles.geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < positions.count; i++) {
        const y = positions.getY(i) + Math.sin(elapsed * 0.8 + i) * dt * 0.15;
        positions.setY(i, THREE.MathUtils.clamp(y, 0.2, 4));
      }
      positions.needsUpdate = true;
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
  }
}
